import { Component, computed, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Auth, user } from '@angular/fire/auth';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faGoogle, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';

import { LoginComponent } from './components/login/login.component';
import {
  selectDev,
  selectLang,
  selectTheme,
  setLang,
  setTheme,
  State,
} from './store/global';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatListModule,
    MatSidenavModule,
    MatSlideToggleModule,
    FaIconComponent,
    TranslateModule,
  ],
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly store = inject(Store<State>);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly afAuth = inject(Auth);

  formDarkTheme = new FormControl(
    window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  formEnglish = new FormControl(false);

  // Signal-based state
  private readonly routerEvents = toSignal(
    this.router.events.pipe(
      filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd),
    ),
  );
  readonly shouldEmphasizeSideNav = computed(
    () => !this.routerEvents()?.urlAfterRedirects.endsWith('home'),
  );

  private readonly firebaseUser = toSignal(user(this.afAuth));
  readonly providerIcon = computed(() => {
    const u = this.firebaseUser();
    const providerId = u?.providerData[0]?.providerId;
    if (providerId?.includes('google')) {
      return faGoogle;
    } else if (providerId?.includes('twitter')) {
      return faTwitter;
    } else {
      return null;
    }
  });

  faTwitter = faTwitter;
  faGoogle = faGoogle;

  readonly dev = this.store.selectSignal(selectDev);
  private readonly theme = this.store.selectSignal(selectTheme);
  private readonly lang = this.store.selectSignal(selectLang);

  constructor() {
    this.translate.setDefaultLang('ja');
    this.translate.use('ja');

    // Sync theme from store to form
    effect(() => {
      const theme = this.theme();
      this.formDarkTheme.patchValue(theme === 'dark', { emitEvent: false });
    });

    // Sync lang from store to form and translate service
    effect(() => {
      const lang = this.lang();
      this.translate.use(lang);
      this.formEnglish.setValue(lang === 'en', { emitEvent: false });
    });
  }

  ngOnInit(): void {
    // Sync form changes to store
    this.formDarkTheme.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        tap((isDark) =>
          this.store.dispatch(setTheme({ theme: isDark ? 'dark' : 'light' })),
        ),
      )
      .subscribe();

    this.formEnglish.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        tap((useEnglish) =>
          this.store.dispatch(setLang({ lang: useEnglish ? 'en' : 'ja' })),
        ),
      )
      .subscribe();
  }

  async login() {
    if (await this.afAuth.currentUser) {
      return;
    }
    this.dialog.open(LoginComponent);
  }

  logout() {
    this.afAuth.signOut();
  }
}
